/**
 * M2.1 Stage A — Customer feature visibility registry.
 *
 * SAFEGUARD: unfinished customer modules must never become reachable by
 * accident (a stray <Link>, a hand-typed URL, a copied nav array). Every
 * customer-account module is declared here and is OFF unless its
 * implementation phase has been explicitly approved.
 *
 * Rules for changing this file:
 *   1. Flipping a module to `true` requires written approval for that phase.
 *   2. A module with `enabled: false` must have NO route file on disk, with
 *      one documented exception: "orders" (M2.1 Stage B Phase 4) ships its
 *      route files behind CUSTOMER_ORDERS_ENABLED, which redirects to
 *      /account while the flag is false.
 *   3. Disabled modules render as non-interactive "Coming soon" entries in
 *      the account navigation — never as links.
 *
 * This module is intentionally dependency-free and imports nothing from
 * checkout, payments, notifications or email.
 */

/**
 * M2.1 Stage B Phase 4 — customer order history master switch.
 *
 * Deliberately a hardcoded literal: no env var, no build flag, no runtime
 * config, no database setting. The feature therefore cannot be turned on by
 * a deployment, an environment difference or an admin action — only by an
 * explicitly approved code change to this line. Defaults to false in every
 * environment (dev, preview, production).
 */
export const CUSTOMER_ORDERS_ENABLED = true as boolean;


export type CustomerFeatureKey =
  | "dashboard"
  | "profile"
  | "addresses"
  | "orders"
  | "preferences"
  | "subscriptions"
  | "tiffin"
  | "wallet"
  | "loyalty"
  | "referrals"
  | "invoices"
  | "support"
  | "aiRecommendations"
  | "appointments";

export type CustomerFeature = {
  key: CustomerFeatureKey;
  label: string;
  /** Route path. `null` while the module is unbuilt — nothing to link to. */
  path: string | null;
  /** Hard visibility gate. Only an approved phase may flip this to true. */
  enabled: boolean;
  /** Phase that owns delivery of this module. */
  phase: string;
  /** Shown in the account nav for disabled modules. */
  comingSoonNote?: string;
};

export const CUSTOMER_FEATURES: readonly CustomerFeature[] = [
  { key: "tiffin", label: "Tiffin subscriptions", path: "/account/tiffin", enabled: true, phase: "Isolated tiffin" },
  // ---- Approved and shipped in Stage A -------------------------------
  { key: "dashboard", label: "Dashboard", path: "/account", enabled: true, phase: "M2.1 Stage A" },
  {
    key: "profile",
    label: "Profile",
    path: "/account/profile",
    enabled: true,
    phase: "M2.1 Stage A",
  },
  {
    key: "addresses",
    label: "Addresses",
    path: "/account/addresses",
    enabled: true,
    phase: "M2.1 Stage A",
  },

  // ---- NOT APPROVED — must stay unreachable --------------------------
  {
    key: "orders",
    label: "Orders",
    path: CUSTOMER_ORDERS_ENABLED ? "/account/orders" : null,
    enabled: CUSTOMER_ORDERS_ENABLED,
    phase: "M2.1 Stage B",
    comingSoonNote: "Order history arrives once orders are linked to your account.",
  },
  {
    key: "preferences",
    label: "Food preferences",
    path: null,
    enabled: false,
    phase: "M2.2",
    comingSoonNote: "Saved dietary preferences are on the way.",
  },
  {
    key: "subscriptions",
    label: "Subscriptions",
    path: "/account/subscriptions",
    enabled: true,
    phase: "M2.1 Stage A",
  },
  // ---- Approved in M2.3 Wallet ---------------------------------------
  {
    key: "wallet",
    label: "Wallet",
    path: "/account/wallet",
    enabled: false,
    phase: "M2.3 Wallet",
  },
  {
    key: "loyalty",
    label: "Loyalty",
    path: null,
    enabled: false,
    phase: "M2.3",
    comingSoonNote: "Rewards for regulars are being cooked up.",
  },
  {
    key: "referrals",
    label: "Referrals",
    path: null,
    enabled: false,
    phase: "M2.3",
    comingSoonNote: "Invite friends — coming soon.",
  },
  {
    key: "invoices",
    label: "Invoices",
    path: null,
    enabled: false,
    phase: "M2.4",
    comingSoonNote: "Downloadable invoices are planned.",
  },
  {
    key: "support",
    label: "Support",
    path: null,
    enabled: false,
    phase: "M2.4",
    comingSoonNote: "In-account support requests are planned.",
  },
  {
    key: "aiRecommendations",
    label: "AI recommendations",
    path: null,
    enabled: false,
    phase: "M2.4",
    comingSoonNote: "Personalised suggestions are planned.",
  },
  {
    key: "appointments",
    label: "Appointments",
    path: "/account/appointments",
    enabled: true,
    phase: "M2.5 Appointments",
  },
] as const;

const FEATURE_BY_KEY = new Map<CustomerFeatureKey, CustomerFeature>(
  CUSTOMER_FEATURES.map((f) => [f.key, f]),
);

export function isCustomerFeatureEnabled(key: CustomerFeatureKey): boolean {
  return FEATURE_BY_KEY.get(key)?.enabled === true;
}

export function getCustomerFeature(key: CustomerFeatureKey): CustomerFeature | undefined {
  return FEATURE_BY_KEY.get(key);
}

/** Modules the account navigation may render as real links. */
export function enabledCustomerFeatures(): CustomerFeature[] {
  return CUSTOMER_FEATURES.filter((f) => f.enabled && f.path !== null);
}

/** Modules rendered as disabled "Coming soon" rows — never links. */
export function upcomingCustomerFeatures(): CustomerFeature[] {
  return CUSTOMER_FEATURES.filter((f) => !f.enabled);
}
